import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import { useAuth } from '../context/AuthContext.js';
import './PracticeTally.css';

const APPARATUS = [
  { id: '', label: 'Any', emoji: '•' },
  { id: 'vault', label: 'Vault', emoji: '🐎' },
  { id: 'bars', label: 'Bars', emoji: '➰' },
  { id: 'beam', label: 'Beam', emoji: '🎋' },
  { id: 'floor', label: 'Floor', emoji: '🤸' },
];
// Common "what they did" quick-picks (coach can also type a custom label)
const QUICK_LABELS = ['Dismount', 'Full routine', 'Vault', 'Series', 'Tumbling pass', 'Skill'];
const initials = (n = '') => n.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?';
const today = () => new Date().toISOString().slice(0, 10);

function PracticeTally() {
  const toast = useToast();
  const { user } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState(null);   // null until defaulted; '' = whole gym
  const [date, setDate] = useState(today());
  const [counts, setCounts] = useState({});
  const [recent, setRecent] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // athlete being tallied
  const [form, setForm] = useState({ apparatus: '', skill_label: '' });

  // Load the coach's classes; default to their OWN rotation (else whole gym)
  useEffect(() => {
    api.get(`${API_BASE_URL}/api/classes?active_only=true`).then(r => {
      const list = Array.isArray(r.data) ? r.data : [];
      setClasses(list);
      const mine = list.filter(c => c.coach_id === user?.id || c.assistant_coach_id === user?.id);
      setClassId(prev => prev !== null ? prev : (mine[0]?.id ?? (list[0]?.id ?? '')));
    }).catch(() => setClassId(''));
  }, [user?.id]);

  // Scope the athlete list to the selected rotation (class roster) or whole gym
  useEffect(() => {
    if (classId === null) return; // wait for default
    if (classId === '' ) {
      api.get(`${API_BASE_URL}/athletes`).then(r => setAthletes(r.data || [])).catch(() => setAthletes([]));
    } else {
      api.get(`${API_BASE_URL}/api/classes/${classId}/roster`)
        .then(r => setAthletes((r.data || []).map(x => ({ id: x.athlete_id, name: x.athlete_name }))))
        .catch(() => setAthletes([]));
    }
  }, [classId]);

  const loadTallies = useCallback(async () => {
    if (classId === null) return;
    try {
      setLoading(true);
      const cq = classId ? `&class_id=${classId}` : '';
      const res = await api.get(`${API_BASE_URL}/api/practice-tally?date=${date}${cq}`);
      setCounts(res.data.counts || {});
      setRecent(res.data.recent || []);
      setTotal(res.data.total || 0);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [date, classId]);

  useEffect(() => { loadTallies(); }, [loadTallies]);

  const openAthlete = (a) => { setActive(a); /* keep last-used event/label for fast repeat */ };

  const addTally = async () => {
    if (!active) return;
    try {
      const res = await api.post(`${API_BASE_URL}/api/practice-tally`, {
        athlete_id: active.id, apparatus: form.apparatus || null,
        skill_label: form.skill_label || null, date, class_id: classId || null,
      });
      // optimistic update
      setCounts(c => ({ ...c, [active.id]: res.data.count }));
      setTotal(t => t + 1);
      setRecent(r => [{
        id: res.data.id, athlete_id: active.id, name: active.name,
        apparatus: form.apparatus || null, skill_label: form.skill_label || null,
        created_at: new Date().toISOString(),
      }, ...r].slice(0, 30));
      toast?.success?.(`+1 ${active.name.split(' ')[0]}${form.skill_label ? ` · ${form.skill_label}` : ''}`);
    } catch (err) {
      toast?.error?.(err.response?.data?.error || 'Failed to tally');
    }
  };

  const undo = async (t) => {
    try {
      await api.delete(`${API_BASE_URL}/api/practice-tally/${t.id}`);
      setRecent(r => r.filter(x => x.id !== t.id));
      setCounts(c => ({ ...c, [t.athlete_id]: Math.max(0, (c[t.athlete_id] || 1) - 1) }));
      setTotal(x => Math.max(0, x - 1));
    } catch {
      toast?.error?.('Failed to undo');
    }
  };

  const appMeta = (id) => APPARATUS.find(a => a.id === (id || '')) || APPARATUS[0];
  const sorted = [...athletes].sort((a, b) => (counts[b.id] || 0) - (counts[a.id] || 0) || a.name.localeCompare(b.name));

  return (
    <div className="pt-page">
      <div className="pt-bar">
        <div className="pt-bar-left">
          <label className="pt-date">Rotation
            <select value={classId ?? ''} onChange={e => setClassId(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value="">All athletes (whole gym)</option>
              {classes.map(c => {
                const mine = c.coach_id === user?.id || c.assistant_coach_id === user?.id;
                return <option key={c.id} value={c.id}>{c.class_name}{mine ? ' (mine)' : ''}</option>;
              })}
            </select>
          </label>
          <label className="pt-date">Session
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </label>
          <span className="pt-total">{total} in rotation{date === today() ? ' today' : ''}</span>
        </div>
        <p className="pt-hint">Tap your name, pick the event and what you did — it adds a tally.</p>
      </div>

      {/* Athlete tiles */}
      {loading && athletes.length === 0 ? (
        <div className="pt-loading">Loading…</div>
      ) : athletes.length === 0 ? (
        <div className="pt-empty">No athletes on the roster yet.</div>
      ) : (
        <div className="pt-grid">
          {sorted.map(a => {
            const c = counts[a.id] || 0;
            return (
              <button key={a.id} className={`pt-tile ${c > 0 ? 'has' : ''}`} onClick={() => openAthlete(a)}>
                <span className="pt-avatar">{initials(a.name)}</span>
                <span className="pt-name">{a.name}</span>
                <span className="pt-count">{c}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Recent feed with undo */}
      {recent.length > 0 && (
        <div className="pt-recent">
          <div className="pt-recent-title">Recent</div>
          <div className="pt-recent-list">
            {recent.map(t => (
              <div key={t.id} className="pt-recent-item">
                <span>{appMeta(t.apparatus).emoji} <strong>{t.name}</strong>{t.skill_label ? ` · ${t.skill_label}` : ''}</span>
                <button className="pt-undo" onClick={() => undo(t)} title="Undo">✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tally panel for the tapped athlete */}
      {active && (
        <div className="pt-overlay" onClick={() => setActive(null)}>
          <div className="pt-panel" onClick={e => e.stopPropagation()}>
            <div className="pt-panel-head">
              <div className="pt-panel-who">
                <span className="pt-avatar big">{initials(active.name)}</span>
                <div>
                  <div className="pt-panel-name">{active.name}</div>
                  <div className="pt-panel-count">{counts[active.id] || 0} today</div>
                </div>
              </div>
              <button className="pt-close" onClick={() => setActive(null)}>✕</button>
            </div>

            <div className="pt-section-label">Event</div>
            <div className="pt-chips">
              {APPARATUS.map(ap => (
                <button key={ap.id} className={`pt-chip ${form.apparatus === ap.id ? 'on' : ''}`}
                  onClick={() => setForm(f => ({ ...f, apparatus: ap.id }))}>{ap.emoji} {ap.label}</button>
              ))}
            </div>

            <div className="pt-section-label">What did you do?</div>
            <div className="pt-chips">
              {QUICK_LABELS.map(l => (
                <button key={l} className={`pt-chip ${form.skill_label === l ? 'on' : ''}`}
                  onClick={() => setForm(f => ({ ...f, skill_label: l }))}>{l}</button>
              ))}
            </div>
            <input className="pt-custom" placeholder="…or type a custom label (optional)"
              value={form.skill_label} onChange={e => setForm(f => ({ ...f, skill_label: e.target.value }))} />

            <button className="pt-add" onClick={addTally}>＋ Add tally</button>
            <p className="pt-panel-hint">Keep tapping “Add tally” to count multiple. Close when done.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default PracticeTally;
